import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import { useTranslation } from 'react-i18next'

const MAX_CONCURRENT_UPLOADS = 4
const PER_FILE_TIMEOUT_MS = 120000
const MAX_ATTEMPTS = 2

const mapWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length)
  let next = 0
  const run = async () => {
    while (next < items.length) {
      const index = next++
      results[index] = await worker(items[index], index)
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, run)
  await Promise.all(workers)
  return results
}

export default function UploadWidget ({ apiBaseUrl, eventId, organizationName }) {
  const { t } = useTranslation('race')
  const g = (key, opts) => t(`mediaGallery.${key}`, opts)
  const [files, setFiles] = useState(null)
  const [photographerName, setPhotographerName] = useState('')
  const [instagramSlug, setInstagramSlug] = useState('')
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [log, setLog] = useState([])
  const appendLog = (s) => {
    const timestamp = new Date().toLocaleTimeString()
    const logEntry = `[${timestamp}] ${s}\n`
    setLog(L => [logEntry, ...L].slice(0, 100))
  }

  const getPresignedUrl = async (file, signal) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/presign-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          photographerId: photographerName,
          instagramSlug,
          organizationName,
          contentType: file.type,
          fileName: file.name
        }),
        signal
      })

      if (!response.ok) {
        const errorBody = await response.text()
        throw new Error(`Presign request failed: ${response.status} - ${errorBody}`)
      }

      const data = await response.json()
      if (!data.url || !data.key) {
        throw new Error('Invalid presign response from server')
      }

      return data
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error(g('request_timeout'))
      }
      if (error.name === 'TypeError') {
        throw new Error(g('network_error'))
      }
      throw error
    }
  }

  const uploadFileToS3 = async (url, file, signal) => {
    const headers = {
      'Content-Type': file.type || 'image/jpeg'
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: file,
      signal
    })

    if (!response.ok) {
      const errorDetails = await response.text()
      console.error('Upload failed with:', {
        status: response.status,
        headers: response.headers,
        error: errorDetails
      })
      throw new Error(`Upload failed: ${response.status}`)
    }

    return response
  }

  const validateFile = (file) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
    const ALLOWED_TYPES = [
      'image/jpeg',
      'image/png'
    ]

    if (!file.type) {
      throw new Error(g('file_type_missing'))
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error(g('invalid_file_type', {
        type: file.type,
        allowed: ALLOWED_TYPES.join(', ')
      }))
    }

    if (file.size === 0) {
      throw new Error(g('file_empty'))
    }

    if (file.size > MAX_FILE_SIZE) {
      throw new Error(g('file_too_large', {
        size: (file.size / 1024 / 1024).toFixed(2),
        max: MAX_FILE_SIZE / 1024 / 1024
      }))
    }
  }

  const uploadOne = async (file) => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), PER_FILE_TIMEOUT_MS)
    try {
      validateFile(file)
      const { url, key } = await getPresignedUrl(file, controller.signal)
      await uploadFileToS3(url, file, controller.signal)
      return { success: true, fileName: file.name, key }
    } finally {
      clearTimeout(timeoutId)
    }
  }

  const onUpload = async () => {
    const missingFields = []
    if (!files) missingFields.push(g('files'))
    if (!eventId) missingFields.push(g('event_id'))
    if (!photographerName) missingFields.push(g('photographer_name'))
    if (!organizationName) missingFields.push(g('organization_name'))

    if (missingFields.length > 0) {
      appendLog(g('missing_fields', { fields: missingFields.join(', ') }))
      return
    }

    const list = Array.from(files)
    setBusy(true)
    setProgress({ done: 0, total: list.length })
    appendLog(g('upload_started', { total: list.length }))

    try {
      // Upload a few files at a time instead of all at once. Firing hundreds of
      // parallel requests makes the browser queue them behind its connection limit
      // and a single global timeout then aborts everything that has not finished.
      const results = await mapWithConcurrency(list, MAX_CONCURRENT_UPLOADS, async (file) => {
        let result
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          try {
            result = await uploadOne(file)
            break
          } catch (error) {
            result = { success: false, fileName: file.name, error: error.stack || error.message }
            if (error.name === 'AbortError' || attempt === MAX_ATTEMPTS) break
          }
        }
        if (result.success) {
          appendLog(`${g('uploaded_to')} ${result.key}`)
        } else {
          appendLog(`${g('upload_failed_for')} ${file.name}: ${result.error}`)
        }
        setProgress(prev => ({ ...prev, done: prev.done + 1 }))
        return result
      })

      const successfulUploads = results.filter(result => result.success)
      const failedUploads = results.filter(result => !result.success)

      appendLog(g('upload_summary', {
        total: results.length,
        success: successfulUploads.length,
        failed: failedUploads.length
      }))

      if (failedUploads.length > 0) {
        appendLog(g('some_uploads_failed', {
          count: failedUploads.length,
          files: failedUploads.map(f => f.fileName).join(', ')
        }))
      } else {
        appendLog(g('all_uploads_successful'))
      }
    } catch (error) {
      appendLog(`${g('upload_error')}: ${error.stack || error.message}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      <Typography variant='h6'>{g('photographer_upload')}</Typography>
      <TextField label={g('photographer_name')} value={photographerName} onChange={e => setPhotographerName(e.target.value)} slotProps={{ htmlInput: { maxLength: 20 } }} />
      <TextField label={g('instagram_slug')} value={instagramSlug} onChange={e => setInstagramSlug(e.target.value)} slotProps={{ htmlInput: { maxLength: 32 } }} />
      <Button variant='outlined' component='label'>{g('choose_photos')}<input hidden type='file' accept='image/*' multiple onChange={e => setFiles(e.target.files)} /></Button>
      <Button variant='contained' onClick={onUpload} disabled={!files || !photographerName || busy}>{busy ? `${g('uploading')} ${progress.done}/${progress.total}` : g('upload')}</Button>
      {busy && <LinearProgress variant='determinate' value={progress.total ? (progress.done / progress.total) * 100 : 0} />}
      <Box component='pre' sx={{ fontSize: 12, maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper', p: 1 }}>{log.join('')}</Box>
    </Stack>
  )
}
